<script lang="ts" setup>
  import CommunityButton from '@/components/common/buttons/CommunityButton.vue'
  import { app } from '@/composition/app'
  import { Target } from '@/types'
  import { Capacitor } from '@capacitor/core'

  import { UIForm, UIFormItem, UIInput, UIModal, UIModalHeader, UITextLgMedium } from '@gohighlevel/ghl-ui'

  import { FormInst, FormRules } from 'naive-ui'

  import { computed, onMounted, ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  import CommunityBottomSheet from '@/components/common/CommunityBottomSheet.vue'
  import { isValidHref, normalizeHref } from '@/components/editor/embed-editors/linkHref'

  interface LinkEmbedAttributes {
    href: string
    target: Target
    noFollow: boolean
    text: string
  }

  const props = defineProps({
    props: String,
    meta: Object,
    id: {
      type: String,
      default: 'link-embed-drawer',
    },
    isCommentEditor: {
      type: Boolean,
      default: false,
    },
  })

  const emit = defineEmits(['add:embed', 'update:close-embed'])
  const { t } = useI18n()
  const defaultValues = {
    href: '',
    target: '_blank',
    text: '',
  }

  const formRef = ref<{ getForm: () => FormInst } | null>(null)
  const placeholder = ref(t('embeds.placeholder'))
  const formValues = ref<LinkEmbedAttributes>(Object.create(defaultValues) as LinkEmbedAttributes)
  const inputRef = ref<HTMLInputElement | null>(null) // Add this line
  const isIos = computed(() => Capacitor.getPlatform() === 'ios')
  const showBottomSheet = ref(false)

  onMounted(() => {
    console.info(props.meta?.initialValues)
    updateDefaultValues()
    if (app.state.isMobile) {
      showBottomSheet.value = true
    }
  })

  function updateDefaultValues() {
    formValues.value = props.meta?.initialValues ? props.meta.initialValues : Object.create(defaultValues)
    placeholder.value = props.meta?.placeholder ? props.meta.placeholder : placeholder.value
  }

  const rules: FormRules = {
    href: {
      required: true,
      trigger: ['change', 'blur'],
      message: t('embeds.validUrl'),
      validator: (_, value: string) => (isValidHref(value) ? Promise.resolve() : Promise.reject(new Error(t('embeds.validUrl')))),
    },
    text: {
      required: true,
      trigger: ['change', 'blur'],
      message: t('embeds.enterSomeText'),
    },
  }

  const onSubmit = () => {
    formRef.value
      ?.getForm()
      .validate()
      .then(() => {
        formValues.value.href = normalizeHref(formValues.value.href)
        emit('add:embed', formValues.value)
        handleCancel()
      })
      .catch(() => {
        console.info('Form is invalid')
      })
  }

  const handleCancel = () => {
    formValues.value = Object.create(defaultValues) // Reset form
    formRef.value?.getForm().restoreValidation()
    showBottomSheet.value = false
    emit('update:close-embed')
  }
</script>
<template>
  <CommunityBottomSheet
    v-if="app.state.isMobile"
    v-model="showBottomSheet"
    :swipe-to-close="true"
    :is-full-screen="false"
    :is-backdrop="true"
    :is-drag-handle="true"
    :is-scroll-lock="true"
    :snap-point="'auto'"
  >
    <template #header>
      <div class="mb-3 flex items-center justify-center">
        <UITextLgMedium class="text-communities-font-primary">
          {{ t('embeds.addLink') }}
        </UITextLgMedium>
      </div>
    </template>
    <div class="px-4">
      <UIForm :id="`${id}_form`" ref="formRef" :model="formValues" :rules="rules">
        <UIInput
          :id="`${id}_text`"
          ref="inputRef"
          v-model="formValues.text"
          :placeholder="t('embeds.text')"
          size="large"
          :min="0"
          class="mb-2"
        />
        <UIInput :id="`${id}_href`" v-model="formValues.href" :placeholder="placeholder" size="large" :min="0" />
      </UIForm>

      <div class="my-4 flex h-full w-full flex-row items-center justify-end gap-4">
        <CommunityButton
          :id="`${id}_cancel_btn`"
          button-type="tertiary"
          class="!h-8 cursor-pointer hover:text-communities-font-primary md:!h-10"
          size="medium"
          @click="handleCancel"
        >
          {{ t('common.cancel') }}
        </CommunityButton>
        <CommunityButton
          :id="`${id}_save_btn`"
          :button-type="'primary'"
          class="!h-8 cursor-pointer hover:text-communities-font-primary md:!h-10"
          size="medium"
          @click="onSubmit"
        >
          {{ t('common.save') }}
        </CommunityButton>
      </div>
    </div>
  </CommunityBottomSheet>

  <UIModal
    v-else
    :id="id"
    :class-name="isIos ? 'ios-modal p-4 md:p-6 max-w-[75%] md:max-w-full' : 'p-4 md:p-6 max-w-[75%] md:max-w-full'"
    :show="true"
    :z-index="props.isCommentEditor ? 2005 : 2003"
    :mask="props.isCommentEditor"
  >
    <!-- <UIModalHeader id="modal-header" title="Add Link"> </UIModalHeader> -->
    <template #header>
      <UIModalHeader id="modal-header" type="custom">
        <template #customHeader>
          <div class="mb-2 flex items-center justify-center text-communities-font-primary">
            <UITextLgMedium>{{ t('embeds.addLink') }}</UITextLgMedium>
          </div>
        </template>
      </UIModalHeader>
    </template>
    <UIForm id="link-embed_form" ref="formRef" :model="formValues" :rules="rules">
      <div v-if="!app.state.isPwa">
        <UIFormItem :label="t('common.ariaLabels.text')" path="text" :rule="rules">
          <UIInput id="editor_link-embed-text" v-model="formValues.text" :placeholder="t('embeds.text')" size="large" :min="0" />
        </UIFormItem>
        <UIFormItem :label="t('common.ariaLabels.link')" path="href" :rule="rules">
          <UIInput id="editor_link-embed-href" v-model="formValues.href" :placeholder="placeholder" size="large" :min="0" />
        </UIFormItem>
      </div>
      <div v-else>
        <UIInput
          id="editor_link-embed-text"
          ref="inputRef"
          v-model="formValues.text"
          :placeholder="t('embeds.text')"
          size="large"
          :min="0"
        />
        <UIInput id="editor_link-embed-href" v-model="formValues.href" class="mt-2" :placeholder="placeholder" size="large" :min="0" />
      </div>
    </UIForm>

    <div class="mt-4 flex h-full w-full flex-row items-center justify-end gap-4">
      <CommunityButton
        id="link__btn"
        button-type="tertiary"
        class="!h-8 cursor-pointer hover:text-communities-font-primary md:!h-10"
        size="medium"
        @click="handleCancel"
      >
        {{ t('common.cancel') }}
      </CommunityButton>
      <CommunityButton
        id="link__btn"
        :button-type="'primary'"
        class="!h-8 cursor-pointer hover:text-communities-font-primary md:!h-10"
        size="medium"
        @click="onSubmit"
      >
        {{ t('common.save') }}
      </CommunityButton>
    </div>

    <!-- </UIDrawerContent> -->
  </UIModal>
</template>
<style scoped>
  .n-card__content,
  .n-card-header,
  .n-card__footer,
  .n-modal {
    padding: 0px !important;
  }
</style>

<style>
  .n-card.n-modal.ios-modal,
  div.ios-modal,
  .ios-modal {
    transform: translateY(0) !important;
    position: relative !important;
    margin-top: 100px !important;
  }
</style>
